import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setDefaultLlmProfile } from '@/lib/api/generated/api';

import { toLlmProfileError } from '../errors';
import { llmProfileKeys } from '../queryKeys';
import type {
  LlmProfile,
  LlmProfileAdminOverview,
  LlmProfileAdminOverviewItem,
} from '../types';

/**
 * Mark one LLM Profile as the default for its model (spec FR-015a).
 *
 * The backend flips the previous default for that `model` `false` and this
 * one `true` atomically, answering with `204 No Content` (mirroring
 * `useSetDefaultHarness`). Optimistically applies the same swap, scoped
 * to profiles sharing the mutated profile's `model`, with `onError`
 * rollback to the snapshots taken in `onMutate`.
 *
 * The swap reaches **two** keys (spec 136 FR-018b): `list()`, read by the
 * workflow builder's step editor, and `adminOverview()`, read by the admin
 * grid. Both, not one: the grid no longer reads `list()`, so applying the swap
 * there alone would make "Set as default" show nothing until `onSettled`'s
 * refetch landed, where today the badge flips instantly. That is a user-visible
 * regression on the exact flow FR-018 protects, so this change exists to
 * *preserve* behaviour rather than to alter it. `onSettled` is untouched — it
 * already invalidates `all()`, which covers both keys.
 */
export function useSetDefaultLlmProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string): Promise<void> => {
      const response = await setDefaultLlmProfile(slug);
      if (response.status === 204) return;
      throw toLlmProfileError(response.status, response.data);
    },
    onMutate: async (slug: string) => {
      await queryClient.cancelQueries({ queryKey: llmProfileKeys.list() });
      await queryClient.cancelQueries({
        queryKey: llmProfileKeys.adminOverview(),
      });

      // Whether the optimistic swap reached anything at all. Tracked because a slug in
      // neither cache means "Set as default" produces no visible change until
      // `onSettled`'s refetch lands, which reads as a dead button — and used to do so
      // with nothing recorded anywhere (R-F56).
      let swapped = false;

      const previous = queryClient.getQueryData<LlmProfile[]>(
        llmProfileKeys.list(),
      );
      if (previous) {
        const target = previous.find((profile) => profile.slug === slug);
        if (target) {
          swapped = true;
          queryClient.setQueryData<LlmProfile[]>(
            llmProfileKeys.list(),
            previous.map((profile) =>
              profile.model === target.model
                ? { ...profile, is_default: profile.slug === slug }
                : profile,
            ),
          );
        }
      }

      // The same per-model swap inside the grid's envelope. The target is
      // looked up in *this* response rather than reused from `list()`, because
      // either key can be in the cache without the other — the grid mounts
      // without the step editor, and vice versa.
      const previousOverview =
        queryClient.getQueryData<LlmProfileAdminOverview>(
          llmProfileKeys.adminOverview(),
        );
      if (previousOverview) {
        // A deleted profile's entry has no slug and no default (spec 258), so only
        // live entries take part in the swap.
        const target = previousOverview.items.find(
          (item): item is LlmProfileAdminOverviewItem =>
            !item.deleted && item.slug === slug,
        );
        if (target) {
          swapped = true;
          queryClient.setQueryData<LlmProfileAdminOverview>(
            llmProfileKeys.adminOverview(),
            {
              ...previousOverview,
              items: previousOverview.items.map((item) =>
                !item.deleted && item.model === target.model
                  ? { ...item, is_default: item.slug === slug }
                  : item,
              ),
            },
          );
        }
      }

      if (!swapped) {
        // A stale cache, or a row the grid has since dropped. The request still goes
        // out and `onSettled` still refetches, so the outcome is correct — it is
        // merely slower and, until this line, unobservable (R-F56).
        console.warn(
          `Set-default found no cached LLM profile for ${JSON.stringify(slug)} in either the list or the admin overview, so nothing changed until the refetch landed.`,
        );
      }

      return { previous, previousOverview };
    },
    onError: (error, slug, context) => {
      console.error(
        `Could not set ${JSON.stringify(slug)} as the default LLM profile for its model; the optimistic swap has been rolled back.`,
        error,
      );

      if (context?.previous) {
        queryClient.setQueryData(llmProfileKeys.list(), context.previous);
      }
      if (context?.previousOverview) {
        queryClient.setQueryData(
          llmProfileKeys.adminOverview(),
          context.previousOverview,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: llmProfileKeys.all() });
    },
  });
}
